import { randomUUID } from "node:crypto";
import type { CnAuth } from "@/lib/cardnexus/auth-context";
import {
  CardNexusError,
  type CnAccount,
  type CnErrorBody,
  type CnInventoryLine,
  type CnList,
  type CnListSummary,
  type CnProductPrices,
  type CnProductSearchHit,
} from "@/lib/cardnexus/types";

export const CN_BASE = "https://public-api.cardnexus.com/v1";
export const CN_GAME = "riftbound";

type CnFetchOptions = RequestInit & {
  /** Skip JSON parse for empty bodies */
  raw?: boolean;
  /** When true, do not send CardNexus-Account (connect/exchange, etc.). */
  noAccount?: boolean;
};

type AuthInput = CnAuth | { credential: string; accountId?: string | null };

function credentialOf(auth: AuthInput): string {
  return auth.credential;
}

function accountOf(auth: AuthInput): string | null {
  return auth.accountId ?? null;
}

export async function cnFetch<T = unknown>(
  path: string,
  auth: AuthInput,
  init?: CnFetchOptions,
): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("Authorization", `Bearer ${credentialOf(auth)}`);
  headers.set("Accept", "application/json");
  if (!headers.has("Accept-Language")) headers.set("Accept-Language", "fr");
  if (init?.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const accountId = accountOf(auth);
  if (accountId && !init?.noAccount) {
    headers.set("CardNexus-Account", accountId);
  }

  const res = await fetch(`${CN_BASE}${path}`, {
    ...init,
    headers,
  });

  if (!res.ok) {
    let body: CnErrorBody | string = `CardNexus ${res.status}`;
    try {
      body = (await res.json()) as CnErrorBody;
    } catch {
      try {
        body = await res.text();
      } catch {
        /* keep default */
      }
    }
    throw new CardNexusError(res.status, body);
  }

  if (res.status === 204 || init?.raw) {
    return undefined as T;
  }

  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export function newIdempotencyKey(prefix = "rifthub"): string {
  return `${prefix}-${randomUUID()}`;
}

export async function exchangeConnectCode(
  credential: string,
  code: string,
): Promise<{ accountId: string; scopes: string[]; grantedAt?: string }> {
  return cnFetch("/connect/exchange", { credential }, {
    method: "POST",
    noAccount: true,
    body: JSON.stringify({ code }),
  });
}

export async function getMe(auth: CnAuth): Promise<CnAccount> {
  return cnFetch<CnAccount>("/account/me", auth);
}

export async function listInventoryPage(
  auth: CnAuth,
  opts: {
    cursor?: string | null;
    limit?: number;
    customId?: string;
    customIdPrefix?: string;
    productId?: number;
    forSale?: boolean;
  } = {},
): Promise<{ data: CnInventoryLine[]; pagination: { nextCursor: string | null } }> {
  const params = new URLSearchParams();
  params.set("game", CN_GAME);
  params.set("limit", String(opts.limit ?? 100));
  if (opts.cursor) params.set("cursor", opts.cursor);
  if (opts.customId) params.set("customId", opts.customId);
  if (opts.customIdPrefix) params.set("customIdPrefix", opts.customIdPrefix);
  if (opts.productId != null) params.set("productId", String(opts.productId));
  if (opts.forSale != null) params.set("forSale", String(opts.forSale));
  return cnFetch(`/inventory?${params.toString()}`, auth);
}

export async function listAllInventory(auth: CnAuth): Promise<CnInventoryLine[]> {
  const lines: CnInventoryLine[] = [];
  let cursor: string | null = null;
  for (let i = 0; i < 500; i++) {
    const page = await listInventoryPage(auth, { cursor, limit: 100 });
    lines.push(...(page.data ?? []));
    cursor = page.pagination?.nextCursor ?? null;
    if (!cursor) break;
  }
  return lines;
}

export async function findInventoryByCustomId(
  auth: CnAuth,
  customId: string,
): Promise<CnInventoryLine | null> {
  const page = await listInventoryPage(auth, { customId, limit: 1 });
  return page.data?.[0] ?? null;
}

export async function getProduct(
  auth: CnAuth,
  productId: number,
): Promise<CnProductSearchHit> {
  return cnFetch(`/products/${productId}`, auth);
}

export async function searchInventory(
  auth: CnAuth,
  body: Record<string, unknown>,
): Promise<{
  data: CnInventoryLine[];
  pagination: { offset: number; limit: number; total: number; hasMore: boolean };
}> {
  return cnFetch("/inventory/search", auth, {
    method: "POST",
    body: JSON.stringify({
      offset: 0,
      limit: 100,
      gameFilters: { game: CN_GAME, filters: {} },
      ...body,
    }),
  });
}

export async function createInventoryLines(
  auth: CnAuth,
  lines: Array<{
    productId: number;
    finish?: string;
    condition?: string;
    language?: string;
    quantity: number;
    customId?: string;
  }>,
): Promise<{ created: CnInventoryLine[]; errors: unknown[] }> {
  return cnFetch("/inventory", auth, {
    method: "POST",
    headers: { "Idempotency-Key": newIdempotencyKey("create") },
    body: JSON.stringify({
      lines: lines.map((line) => ({
        productId: line.productId,
        finish: line.finish ?? "Standard",
        condition: line.condition ?? "NM",
        language: line.language ?? "en",
        quantity: line.quantity,
        ...(line.customId ? { customId: line.customId } : {}),
      })),
    }),
  });
}

export async function updateInventoryLine(
  auth: CnAuth,
  inventoryId: string,
  patch: Record<string, unknown>,
): Promise<{ line: CnInventoryLine }> {
  return cnFetch(`/inventory/${encodeURIComponent(inventoryId)}`, auth, {
    method: "PATCH",
    headers: { "Idempotency-Key": newIdempotencyKey("patch") },
    body: JSON.stringify(patch),
  });
}

export async function deleteInventoryLine(auth: CnAuth, inventoryId: string): Promise<void> {
  await cnFetch(`/inventory/${encodeURIComponent(inventoryId)}`, auth, {
    method: "DELETE",
    headers: { "Idempotency-Key": newIdempotencyKey("del") },
    raw: true,
  });
}

export async function searchProducts(
  auth: CnAuth,
  body: Record<string, unknown>,
): Promise<{ data: CnProductSearchHit[]; pagination?: { hasMore?: boolean } }> {
  return cnFetch("/products/search", auth, {
    method: "POST",
    body: JSON.stringify({
      offset: 0,
      limit: 20,
      gameFilters: { game: CN_GAME, filters: {} },
      productType: { op: "or", values: ["card"] },
      ...body,
    }),
  });
}

export async function getProductsByIds(
  auth: CnAuth,
  productIds: number[],
): Promise<CnProductSearchHit[]> {
  const unique = [...new Set(productIds.filter((id) => Number.isFinite(id) && id > 0))];
  const out: CnProductSearchHit[] = [];
  for (let i = 0; i < unique.length; i += 200) {
    const chunk = unique.slice(i, i + 200);
    const res = await searchProducts(auth, {
      productIds: chunk,
      limit: Math.min(200, chunk.length),
      offset: 0,
      // Pas de filtre "card" : on veut aussi le nom des produits scellés de l'inventaire
      productType: undefined,
    });
    out.push(...(res.data ?? []));
  }
  return out;
}

/** Listes CardNexus (decks, want lists…) du jeu Riftbound. Scope `lists:read`. */
export async function listLists(auth: CnAuth): Promise<CnListSummary[]> {
  const out: CnListSummary[] = [];
  for (let offset = 0; offset < 2000; offset += 200) {
    const params = new URLSearchParams({ game: CN_GAME, limit: "200", offset: String(offset) });
    const res = await cnFetch<{ data: CnListSummary[]; pagination?: { hasMore?: boolean } }>(
      `/lists?${params.toString()}`,
      auth,
    );
    out.push(...(res.data ?? []));
    if (!res.pagination?.hasMore) break;
  }
  return out;
}

export async function getList(auth: CnAuth, listId: string): Promise<CnList> {
  return cnFetch<CnList>(`/lists/${encodeURIComponent(listId)}`, auth);
}

export async function getProductPrices(
  auth: CnAuth,
  productId: number,
): Promise<CnProductPrices> {
  return cnFetch(`/products/${productId}/prices`, auth);
}
